import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TripMap from './components/TripMap';
import TripDetails from './components/TripDetails';
import RentalSelector from './components/RentalSelector';
import StopCustomizer from './components/StopCustomizer';
import TripProgress from './components/TripProgress';
import WorkflowExplanation from './components/WorkflowExplanation';
import { createTrip, setAuthCredentials } from './services/api';
import api from './services/api';

// Create a theme with blue and green colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#2e7d32',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

// Authentication form component
const AuthForm = ({ onLogin, error, loading }) => {
  const [password, setPassword] = useState('');
  const [rememberPassword, setRememberPassword] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(password, rememberPassword);
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 400, mx: 'auto', mt: 8, p: 4 }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <LockIcon color="primary" sx={{ fontSize: 40, mr: 1 }} />
          <Typography variant="h5" component="h1">
            Authentication Required
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please enter the password to access the trip generator.
          Username is already set to "field".
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoFocus
          disabled={loading}
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberPassword}
              onChange={(e) => setRememberPassword(e.target.checked)}
              color="primary"
            />
          }
          label="Remember password"
        />
        
        <Button 
          type="submit" 
          variant="contained" 
          fullWidth 
          size="large"
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
          ) : 'Login'}
        </Button>
      </Box>
    </Paper>
  );
};

function App() {
  // State variables
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRental, setSelectedRental] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [totalKilometers, setTotalKilometers] = useState(0);
  
  // Check authentication status on mount and restore password from localStorage if available
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try to restore saved password from localStorage
        const savedPassword = localStorage.getItem('fahrtenbuchPassword');
        if (savedPassword) {
          console.log("Found saved password, attempting to use it");
          setAuthCredentials('field', savedPassword);
        }
        
        console.log("Checking auth status...");
        const response = await api.get('/');
        console.log("Auth check successful", response);
        setIsAuthenticated(true);
      } catch (error) {
        console.log("Auth check error:", error);
        if (error.response && error.response.status === 401) {
          console.log("Got 401 Unauthorized, showing login screen");
          setIsAuthenticated(false);
          // Clear saved password if it's invalid
          localStorage.removeItem('fahrtenbuchPassword');
        } else {
          // For other errors, assume auth is not the issue
          console.error("Error checking authentication:", error);
          console.log("Assuming auth is valid despite error");
          setIsAuthenticated(true);
        }
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Handle login
  const handleLogin = async (password, rememberPassword = true) => {
    setAuthLoading(true);
    setAuthError(null);
    
    // Update authentication in axios config
    setAuthCredentials('field', password);
    
    try {
      console.log("Attempting login with provided password");
      await api.get('/');
      console.log("Login successful");
      setIsAuthenticated(true);
      setAuthError(null);
      
      // Save password to localStorage for persistence if remember is checked
      if (rememberPassword) {
        localStorage.setItem('fahrtenbuchPassword', password);
        console.log("Password saved to localStorage");
      } else {
        // Clear any saved password if user didn't check "remember"
        localStorage.removeItem('fahrtenbuchPassword');
        console.log("Password not saved (remember option unchecked)");
      }
    } catch (error) {
      console.log("Login failed:", error);
      if (error.response && error.response.status === 401) {
        setAuthError('Invalid password. Please try again.');
      } else {
        setAuthError('Error connecting to server. Please try again later.');
      }
      setIsAuthenticated(false);
      // Remove any saved password if login fails
      localStorage.removeItem('fahrtenbuchPassword');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle selection of a rental
  const handleRentalSelected = async (rental, options = {}) => {
    // Reset any existing trip and progress state
    setTrip(null);
    setShowProgress(false);
    
    // Wait a moment to ensure UI updates before starting a new trip
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Now set new values
    setSelectedRental(rental);
    setLoading(true);
    setError(null);
    setShowProgress(true);
    
    try {
      // Store the total kilometers for display
      setTotalKilometers(rental.total_kilometers);
      
      // Create trip request data with the rental information directly
      // rather than using rental_id
      const requestData = {
        start_location: rental.pickup_location,
        end_location: rental.dropoff_location,
        total_kilometers: rental.total_kilometers
      };
      
      // Add parameters from options object
      
      // Use simple min_stops and max_stops parameters
      if (options.minStops !== undefined && options.minStops !== '') {
        // Parse the value to ensure it's a number
        const minStopsValue = parseInt(options.minStops, 10);
        if (!isNaN(minStopsValue)) {
          requestData.min_stops = minStopsValue;
          console.log(`Setting min_stops = ${minStopsValue}`);
        }
      } 
      
      if (options.maxStops !== undefined && options.maxStops !== '') {
        // Parse the value to ensure it's a number
        const maxStopsValue = parseInt(options.maxStops, 10);
        if (!isNaN(maxStopsValue)) {
          requestData.max_stops = maxStopsValue;
          console.log(`Setting max_stops = ${maxStopsValue}`);
        }
      }
      
      // Add profile if provided (timur or sherry)
      if (options.profile !== undefined) {
        requestData.profile = options.profile;
        console.log(`Using profile: ${options.profile}`);
      }
      
      console.log("Starting trip generation for:", rental.pickup_location, "to", rental.dropoff_location);
      
      // Call API to create trip
      const tripData = await createTrip(requestData);
      console.log("Trip data received:", tripData ? "Success" : "Empty", tripData);
      
      // Validate that we received proper trip data
      if (!tripData || !tripData.trip || tripData.trip.length === 0) {
        throw new Error("No valid trip data was returned from the server. Try again or choose different parameters.");
      }
      
      setTrip(tripData);
      
      // Switch to the map tab after a delay to allow progress to complete
      // When trip exists, map view will be the first tab (index 0)
      setTimeout(() => {
        setActiveTab(0);
      }, 1000);
    } catch (err) {
      // Check for authentication error
      if (err.response && err.response.status === 401) {
        setIsAuthenticated(false);
        setAuthError('Your session has expired. Please log in again.');
        setShowProgress(false);
      } else {
        setError(`Failed to create trip: ${err.message}`);
        console.error('Error creating trip:', err);
        setShowProgress(false);
        
        // Reset to the appropriate tab to show the error
        if (activeTab > 1) {
          setActiveTab(0); // Return to rental selection tab
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle progress completion
  const handleProgressComplete = () => {
    console.log("Progress complete, hiding progress indicator");
    // Hide progress component after a delay
    setTimeout(() => {
      setShowProgress(false);
      
      // Ensure we're showing a valid tab after trip generation
      if (trip && trip.trip && trip.trip.length > 0) {
        // Make sure we're on a valid tab index for when trip exists
        // Valid tabs when trip exists are: 0=MapView, 1=CustomizeStops, 2=TripDetails
        if (activeTab > 2) {
          setActiveTab(0); // First tab is Map View when trip exists
        }
      }
    }, 2000);
  };
  
  
  // Handle trip update from the stop customizer
  const handleUpdateTrip = async (updateData) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Updating trip with new data");
      
      // Call API to update trip
      const updatedTripData = await createTrip(updateData);
      console.log("Updated trip data received:", updatedTripData ? "Success" : "Empty", updatedTripData);
      
      // Validate that we received proper trip data
      if (!updatedTripData || !updatedTripData.trip || updatedTripData.trip.length === 0) {
        throw new Error("No valid trip data was returned from the server. Try again or choose different parameters.");
      }
      
      setTrip(updatedTripData);
    } catch (err) {
      // Check for authentication error
      if (err.response && err.response.status === 401) {
        setIsAuthenticated(false);
        setAuthError('Your session has expired. Please log in again.');
      } else {
        setError(`Failed to update trip: ${err.message}`);
        console.error('Error updating trip:', err);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Show loading indicator while checking authentication
  if (checkingAuth) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Connecting to server...
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }
  
  // Show auth form if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box sx={{ my: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Fahrtenbuch Trip Planner
            </Typography>
            <AuthForm 
              onLogin={handleLogin} 
              error={authError} 
              loading={authLoading} 
            />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }
  
  // Main app when authenticated
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Fahrtenbuch Trip Planner
            </Typography>
            <Button 
              variant="outlined" 
              color="secondary"
              size="small"
              onClick={() => {
                localStorage.removeItem('fahrtenbuchPassword');
                setIsAuthenticated(false);
              }}
            >
              Logout
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Progress tracking */}
          <TripProgress 
            visible={showProgress} 
            onComplete={handleProgressComplete}
          />
          
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            {/* Plan New Trip button shown only when trip data is available */}
            {trip && trip.trip && trip.trip.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => {
                    setTrip(null);
                    setActiveTab(0);
                    setError(null);
                    setShowProgress(false);
                    setSelectedRental(null);
                  }}
                  sx={{ mr: 1 }}
                >
                  Plan New Trip
                </Button>
              </Box>
            )}
            
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="trip creation tabs"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              {/* Only show initial tabs if no trip data or explicitly showing them */}
              {(!trip || !trip.trip || trip.trip.length === 0) && <Tab label="Timur" />}
              {(!trip || !trip.trip || trip.trip.length === 0) && <Tab label="Sherry" />}
              {trip && trip.trip && trip.trip.length > 0 && <Tab label="Map View" />}
              {trip && trip.trip && trip.trip.length > 0 && <Tab label="Customize Stops" />}
              {trip && trip.trip && trip.trip.length > 0 && <Tab label="Trip Details" />}
            </Tabs>
            
            {/* Initial tabs only shown if no trip is generated */}
            {/* Tab 0: Timur Rental Selection */}
            {activeTab === 0 && !trip && (
              <RentalSelector 
                profile="timur"
                onRentalSelected={(rental, options) => handleRentalSelected(rental, {...options, profile: 'timur'})} 
              />
            )}
            
            {/* Tab 1: Sherry Rental Selection */}
            {activeTab === 1 && !trip && (
              <RentalSelector 
                profile="sherry"
                onRentalSelected={(rental, options) => handleRentalSelected(rental, {...options, profile: 'sherry'})} 
              />
            )}
            
            {/* Tab 0: Map View (when trip exists) */}
            {activeTab === 0 && trip && trip.trip && trip.trip.length > 0 ? (
              <TripMap 
                trip={trip.trip} 
                totalKilometers={trip.rental_kilometers || totalKilometers || 0}
                actualKilometers={trip.total_kilometers || 0}
              />
            ) : activeTab === 0 && trip && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  No trip data available
                </Typography>
                <Typography variant="body1">
                  Please go back and select a rental or create a custom trip.
                </Typography>
              </Box>
            )}
            
            {/* Tab 1: Customize Stops (when trip exists) */}
            {activeTab === 1 && trip && trip.trip && trip.trip.length > 0 ? (
              <StopCustomizer trip={trip} onUpdateTrip={handleUpdateTrip} />
            ) : activeTab === 1 && trip && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  No trip data available
                </Typography>
                <Typography variant="body1">
                  Please go back and select a rental or create a custom trip.
                </Typography>
              </Box>
            )}
            
            {/* Tab 2: Trip Details (when trip exists) */}
            {activeTab === 2 && trip && trip.trip && trip.trip.length > 0 ? (
              <TripDetails 
                trip={trip} 
                startKilometer={selectedRental ? selectedRental.start_kilometer : undefined}
                endKilometer={selectedRental ? selectedRental.end_kilometer : undefined}
              />
            ) : activeTab === 2 && trip && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  No trip data available
                </Typography>
                <Typography variant="body1">
                  Please go back and select a rental or create a custom trip.
                </Typography>
              </Box>
            )}
            
            {/* Loading indicator */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  Processing your request...
                </Typography>
              </Box>
            )}
          </Paper>
          
          {/* Workflow Explanation Footer */}
          <WorkflowExplanation />
          
          {/* Copyright/footer section */}
          <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Fahrtenbuch Trip Planner
            </Typography>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;